export type QuoteStatus = 'draft' | 'active' | 'in_progress' | 'completed' | 'canceled'
export type IncotermType = 'FOB' | 'CIF' | 'EXW' | 'FCA' | 'CPT' | 'CIP' | 'DAP' | 'DPU' | 'DDP' | 'FAS' | 'CFR'
export type CargoType = 'geral' | 'empilhavel' | 'perigosa'
export type ModalityType = 'maritimo' | 'aereo' | 'terrestre'
export type OperationType = 'import' | 'export'
export type VolumeType = 'FCL' | 'LCL'

export interface Location {
  location: {
    city: string
    state?: string
    country: string
    zip_code?: string
  }
  contact_name: string
  contact_phone: string
  collect_date?: string
  delivery_date?: string
}

export interface VolumeItem {
  type: string
  quantity: number
  weight: number // Always in kg
  dimensions?: {
    length: number
    width: number
    height: number
    unit: 'cm' | 'm'
  }
  description?: string
  ncm?: string
}

export interface Quote {
  id: string
  tenant_id: string
  shipper_id: string
  user_create: string

  tx_control: string
  year: number
  status: QuoteStatus
  status_reason?: string

  modality_id: string
  modality_name?: ModalityType
  modality_type?: OperationType
  volume_name?: VolumeType

  incoterm: IncotermType

  origin: Location
  destination: Location
  pickup_location?: Location

  goods_description?: string
  goods_ncm?: string
  goods_ncm_list?: string[]
  goods_special: boolean
  goods_type?: CargoType

  volume_items?: VolumeItem[]

  documents?: QuoteDocumentInfo[]

  is_deleted: boolean
  user_cancelled?: string
  dates_cancelled?: string

  shipper_name?: string // Para freight forwarders verem quem criou

  offers_count?: number // Contagem de ofertas recebidas
  sent_offers_count?: number // Contagem de ofertas enviadas (não rascunhos)

  client_reference?: string // Referência interna do embarcador (PO, número de processo, etc.)

  quote_deadline?: string // Data limite para receber ofertas
  request_platform_agents?: boolean // Receber cotações de agentes parceiros Eximtech
  request_insurance_quote?: boolean // Receber cotação de seguro da carga
  notes?: string // Observações do embarcador, visíveis aos agentes

  created_at: string
  updated_at: string
}

export interface QuoteDocumentInfo {
  name: string
  url: string
  key?: string
  mimeType?: string
  type?: string // legacy field
  size?: number
  uploaded_at?: string
  uploaded_by_name?: string
}

export interface Modality {
  id: string
  name: ModalityType
  type: OperationType
  volume_name?: VolumeType
  is_active: boolean
  created_at: string
}

export interface CreateQuoteData {
  modality_id: string
  incoterm: IncotermType
  origin: Location
  destination: Location
  pickup_location?: Location
  goods_description?: string
  goods_ncm?: string
  goods_ncm_list?: string[]
  goods_special?: boolean
  goods_type?: CargoType
  volume_items?: VolumeItem[]
  client_reference?: string
  quote_deadline?: string
  request_platform_agents?: boolean
  request_insurance_quote?: boolean
  goods_value?: number | null
  goods_value_currency?: string
  documents?: QuoteDocumentInfo[]
  notes?: string
}

export interface QuoteFilters {
  status?: QuoteStatus | 'all'
  year?: number
  search?: string
}

// Labels para exibição
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativa',
  in_progress: 'Em Andamento',
  completed: 'Concluída',
  canceled: 'Cancelada'
}

export const INCOTERM_LABELS: Record<IncotermType, string> = {
  FOB: 'FOB - Free On Board',
  CIF: 'CIF - Cost, Insurance and Freight',
  EXW: 'EXW - Ex Works',
  FCA: 'FCA - Free Carrier',
  CPT: 'CPT - Carriage Paid To',
  CIP: 'CIP - Carriage and Insurance Paid To',
  DAP: 'DAP - Delivered At Place',
  DPU: 'DPU - Delivered at Place Unloaded',
  DDP: 'DDP - Delivered Duty Paid',
  FAS: 'FAS - Free Alongside Ship',
  CFR: 'CFR - Cost and Freight'
}

export const MODALITY_LABELS: Record<ModalityType, string> = {
  maritimo: 'Marítimo',
  aereo: 'Aéreo',
  terrestre: 'Terrestre'
}

export const OPERATION_LABELS: Record<OperationType, string> = {
  import: 'Importação',
  export: 'Exportação'
}

export const CARGO_TYPE_LABELS: Record<CargoType, string> = {
  geral: 'Carga Geral',
  empilhavel: 'Empilhável',
  perigosa: 'Perigosa'
}
